/**
 * New text on an approved consent section sets it back to draft, so it has
 * to be approved again (decided 2026-10-01). The server makes the change
 * (functions/src/consent/consentReapproval.ts) and marks the section
 * `needsReapproval`; the page asks before a Regenerate that could do it, and
 * says plainly when it has happened.
 *
 * Everything here is pure, so the page's decisions are tested without React.
 */

interface SectionLike { status?: unknown; needsReapproval?: unknown }

/** Shown on a section that was approved and then given new text. */
export const REOPENED_NOTICE_TEXT =
  'This section was approved. Your change set it back to draft, so it needs approving again.';

/**
 * Asked before Regenerate on an approved section. True for every section: a
 * fixed-text section writes the same text again and stays approved.
 */
export const REGENERATE_APPROVED_CONFIRM_TEXT =
  'This section is approved. Regenerating writes its text again. If the text changes, the section goes back to draft and needs approving again.';

/** Shown when moving on is stopped because the change was refused. */
export const LEAVE_BLOCKED_REFUSED_TEXT =
  'This section was changed somewhere else, so your change was not saved. Keep editing to copy your text first, or discard your change and load the saved version.';

/** Shown when moving on is stopped because the save failed. */
export const LEAVE_BLOCKED_ERROR_TEXT =
  'Your change could not be saved. Keep editing and try again, or discard your change and load the saved version.';

/** Shown with text that could not be saved after the form moved elsewhere. */
export const MOVED_AWAY_UNSAVED_TEXT =
  'The form moved to another section while you had unsaved text, and that text could not be saved. Here it is, so you can copy it.';

/** Regenerate asks first only when the section is approved. */
export function regenerateNeedsConfirm(section: SectionLike | null | undefined): boolean {
  return section?.status === 'approved';
}

/** The notice shows while a reopened section waits to be approved again. */
export function reopenedNoticeNeeded(section: SectionLike | null | undefined): boolean {
  return section?.needsReapproval === true && section.status !== 'approved';
}

export type ConsentLeaveSave = { save: false } | { save: true; reopens: boolean };

/**
 * Whether leaving a section saves the page's copy of its text.
 *
 * Saved ONLY when the user changed the text (the same rule as the DMP
 * panel's section save): text that differs from the stored copy without
 * anyone typing (the page's copy is behind) is not a change, and an
 * approved section nobody touched is never saved and never reopened. An
 * approved section the user did edit IS saved on a real leave (moving to
 * another section, closing the builder, leaving the page), even when
 * emptied, and the save sets it back to draft (`reopens`). A tab merely
 * being hidden (`reason: 'hidden'`) saves draft sections only, so switching
 * tabs never un-approves a section. A draft section left empty is not saved.
 */
export function decideConsentLeaveSave(args: {
  content: string;
  userChanged: boolean;
  stored: { status?: unknown; content?: unknown } | null | undefined;
  reason?: 'leave' | 'hidden';
}): ConsentLeaveSave {
  const { content, userChanged, stored } = args;
  if (!userChanged) return { save: false };
  const storedText = typeof stored?.content === 'string' ? stored.content : '';
  if (content === storedText) return { save: false };
  const approved = stored?.status === 'approved';
  if (approved && args.reason === 'hidden') return { save: false };
  if (!approved && !content) return { save: false };
  return { save: true, reopens: approved };
}

/** What a flush of the section being left came to. */
export type FlushOutcome = 'none' | 'saved' | 'refused' | 'error';

/** Moving to another section goes ahead only when nothing was lost. */
export function navigationAllowedAfterFlush(outcome: FlushOutcome): boolean {
  return outcome === 'none' || outcome === 'saved';
}

export interface ConsentSyncActions {
  /** The text to put in the editor, or undefined to leave it as it is. */
  setContent: string | undefined;
  /** The section's new base version, or undefined to keep the page's. */
  setVersion: number | undefined;
  /** The section whose unsaved mark to clear, if any. */
  clearMarkFor: string | undefined;
  /** A section left with unsaved text, to save before it is replaced. */
  flushPrevious: string | undefined;
}

/**
 * What the editor does when the form is updated (a snapshot arrives).
 *
 * The stored text is taken on for a different section, or when the user has
 * no unsaved change; otherwise the user's text stays and the normal save
 * writes it, and the page's base version does NOT move to the incoming one,
 * so a change made elsewhere refuses that save instead of being overwritten.
 * The base only ever moves forward, so the echo of the user's own save is
 * not a conflict. When the section changes under unsaved text (another tab,
 * a Redraft), that text is saved to the section it belongs to first.
 */
export function consentSyncActions(args: {
  prevSectionId: string | null;
  currentSectionId: string;
  stored: { content?: unknown; draftVersion?: unknown } | null | undefined;
  knownVersion: number;
  userChanged: (sectionId: string) => boolean;
}): ConsentSyncActions {
  const none: ConsentSyncActions = { setContent: undefined, setVersion: undefined, clearMarkFor: undefined, flushPrevious: undefined };
  const { prevSectionId, currentSectionId, stored, knownVersion, userChanged } = args;
  if (!stored) return none;
  const sectionChanged = prevSectionId !== currentSectionId;
  const flushPrevious = sectionChanged && prevSectionId !== null && userChanged(prevSectionId) ? prevSectionId : undefined;
  const adopt = sectionChanged || !userChanged(currentSectionId);
  if (!adopt) return { ...none, flushPrevious };
  const v = stored.draftVersion;
  const storedVersion = typeof v === 'number' && Number.isInteger(v) && v >= 0 ? v : 0;
  return {
    setContent: typeof stored.content === 'string' ? stored.content : '',
    setVersion: storedVersion > knownVersion ? storedVersion : undefined,
    clearMarkFor: currentSectionId,
    flushPrevious,
  };
}

export type BuildAllStep = { id: string; title: string; action: 'generate' | 'skip'; baseDraftVersion: number };

const versionOf = (v: unknown): number => (typeof v === 'number' && Number.isInteger(v) && v >= 0 ? v : 0);

/**
 * What Build All does with each section. Build All writes drafts and never
 * approves anything: a person approves each section (decided 2026-10-01).
 * Approved sections are left alone. A section holding text the user wrote
 * (saved by the user, not empty) is skipped. Every other section is written
 * as a draft, with the version it was planned from, so the server refuses
 * to write over text the user saved since (another tab).
 */
export function buildAllPlan(
  order: Array<{ id: string; title: string }>,
  sections: Record<string, { status?: unknown; content?: unknown; userEdited?: unknown; draftVersion?: unknown } | undefined> | null | undefined,
): BuildAllStep[] {
  const steps: BuildAllStep[] = [];
  for (const s of order) {
    const st = sections?.[s.id];
    if (st?.status === 'approved') continue;
    const userText = st?.userEdited === true && typeof st.content === 'string' && st.content.trim() !== '';
    steps.push({ id: s.id, title: s.title, action: userText ? 'skip' : 'generate', baseDraftVersion: versionOf(st?.draftVersion) });
  }
  return steps;
}

/** The question Build All asks before it starts. */
export function buildAllQuestion(toWrite: number, leftAsTheyAre: string[]): string {
  const first = toWrite === 1
    ? 'Build All writes the remaining 1 section as a draft for you to review and approve.'
    : `Build All writes the remaining ${toWrite} sections as drafts for you to review and approve.`;
  return leftAsTheyAre.length
    ? `${first} Sections holding text you wrote are left as they are: ${leftAsTheyAre.join(', ')}.`
    : first;
}

/** What Build All says when it ends: what was written and what was left. */
export function buildAllSummary(args: {
  ended: 'finished' | 'cancelled' | 'failed';
  failedTitle?: string;
  written: string[];
  left: string[];
}): string {
  const head = args.ended === 'finished'
    ? 'Build All finished.'
    : args.ended === 'cancelled'
      ? 'Build All was cancelled.'
      : `Build All stopped because "${args.failedTitle ?? 'a section'}" could not be written.`;
  const written = args.written.length
    ? `Written as drafts for you to approve: ${args.written.join(', ')}.`
    : 'Nothing was written.';
  const left = args.left.length ? ` Left as they were: ${args.left.join(', ')}.` : '';
  return `${head} ${written}${left}`;
}

export type ApprovableDraft = { id: string; title: string; content: string; baseDraftVersion: number };

/**
 * The drafts "Approve all drafts" would approve, in order: offered only when
 * every section has text and none is waiting to be written, and at least one
 * is still a draft. Each carries its own base version, so a section changed
 * elsewhere is refused rather than approved unseen.
 */
export function approveAllDrafts(
  order: Array<{ id: string; title: string }>,
  sections: Record<string, { status?: unknown; content?: unknown; draftVersion?: unknown } | undefined> | null | undefined,
): ApprovableDraft[] {
  const drafts: ApprovableDraft[] = [];
  for (const s of order) {
    const st = sections?.[s.id];
    const text = typeof st?.content === 'string' ? st.content : '';
    if (!st || st.status === 'pending' || st.status === 'generating' || text.trim() === '') return [];
    if (st.status !== 'approved') drafts.push({ id: s.id, title: s.title, content: text, baseDraftVersion: versionOf(st.draftVersion) });
  }
  return drafts;
}

/** The one question "Approve all drafts" asks. */
export function approveAllQuestion(n: number): string {
  return `Approve all ${n} draft section${n === 1 ? '' : 's'}? Approve each one only after reading it.`;
}

/** What "Approve all drafts" says when it ends. */
export function approveAllSummary(approved: string[], refused: string[]): string {
  const head = `Approved ${approved.length} section${approved.length === 1 ? '' : 's'}.`;
  if (!refused.length) return head;
  const why = refused.length === 1 ? 'it was' : 'they were';
  return `${head} Not approved, because ${why} changed somewhere else: ${refused.join(', ')}.`;
}

/**
 * What the page does after saving text left in a section the form moved
 * away from: a refused or failed save keeps the text on screen to copy; a
 * landed save that reopened an approved section says so.
 */
export function afterPreviousSectionSave(
  result: 'saved' | 'refused' | 'error',
  reopens: boolean,
): { recover: boolean; reopenedNotice: boolean } {
  if (result !== 'saved') return { recover: true, reopenedNotice: false };
  return { recover: false, reopenedNotice: reopens };
}
